/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Input, Spin, Empty, Badge, Upload, Button, message, Progress } from 'antd';
import {
    DownOutlined,
    PictureOutlined,
    LeftOutlined,
    CloseOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';
import { useChatContext } from '@/shared/contexts/chatContext';
import { useMemberContext } from '@/shared/contexts/memberContext';
import chatRoomApi from '@/apis/lib/chat-room';
import chatMessageApi from '@/apis/lib/chat-message';
import {
    countByChatrooms,
    multipleRemove,
    CountByChatroomsResponse,
} from '@/apis/lib/user-notification';
import EventSourcePolyfill from 'eventsource-polyfill';
import { playChatNotificationSound } from '@/shared/helpers/audio';
import APIUtil from '@/shared/helpers/api-utils';
import * as styles from './styles';

const uploaderInfo = APIUtil.getUploaderInfo();

interface ChatRoom {
    id: number;
    userAId: number;
    userBId: number;
    userA: any;
    userB: any;
    notMeUser?: any;
}

interface ChatMessage {
    id: number;
    chatRoomId: number;
    message: string;
    createdAt: string;
    senderId: number;
    imageUrls?: any;
}

const FloatingChatroom: React.FC = () => {
    const { chat, closeFloatingChatroom, resetProxyChatRoomId } = useChatContext();
    const { member } = useMemberContext();
    const [onLoadingChatroom, setOnLoadingChatroom] = useState(false);
    const [onLoadingChatMessage, setOnLoadingChatMessage] = useState(false);
    const [onLoadingSendMessage, setOnLoadingSendMessage] = useState(false);
    const [selectedChatRoom, setSelectedChatRoom] = useState<ChatRoom | null>(null);
    const [filteredNotUserName, setFilteredNotUserName] = useState('');
    const [chatrooms, setChatrooms] = useState<ChatRoom[]>([]);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatTextAreaValue, setChatTextAreaValue] = useState('');
    const [chatImageUrls, setChatImageUrls] = useState<any>(null);
    const [textAreaRow, setTextAreaRow] = useState(1);
    const [userNotificationMap, setUserNotificationMap] = useState<CountByChatroomsResponse[]>([]);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showContactListOnMobile, setShowContactListOnMobile] = useState(true);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const newChatMessageEventRef = useRef<EventSourcePolyfill | null>(null);
    const selectedChatRoomRef = useRef<ChatRoom | null>(null);
    const fetchChatMessagesRef = useRef<((chatRoomId: number) => Promise<void>) | null>(null);
    const renderCountRef = useRef(0);

    const getCurrentMemberId = useCallback((): number | null => {
        // Match public frontend: member.sn (not member.id)
        // From MemberMixin._getCurrentMemberId(): return member ? member.sn : false;
        if (member?.id) {
            return member.id;
        }
        const savedMember = localStorage.getItem('member');
        if (savedMember) {
            try {
                const memberData = JSON.parse(savedMember);
                // Public frontend uses 'sn' field, not 'id'
                return memberData.sn || memberData.id || null;
            } catch (e) {
                console.error('Error parsing member from localStorage:', e);
                return null;
            }
        }
        console.warn('No member found in localStorage');
        return null;
    }, [member]);

    // const getUserFirstChar = (user: any): string => {
    //     if (user?.name) {
    //         return user.name[0].toUpperCase();
    //     }
    //     if (user?.email) {
    //         return user.email[0].toUpperCase();
    //     }
    //     return 'A';
    // };

    const getUserAvatarUrl = (user: any, size: number = 64): string => {
        const name = user?.name || user?.email || 'User';
        // Use ui-avatars.com API to generate avatar with initials
        // Parameters: name, size, rounded (circle), background (#eee), color (black), bold, uppercase
        const encodedName = encodeURIComponent(name);
        return `https://ui-avatars.com/api/?name=${encodedName}&size=${size}&rounded=true&background=eeeeee&color=000000&bold=true&uppercase=true`;
    };

    const getImageUrl = (image: any): string | null => {
        if (!image) return null;
        try {
            if (typeof image === 'string') {
                const parsed = JSON.parse(image);
                return parsed.url;
            }
            return image.url;
        } catch (e) {
            return null;
        }
    };

    const beforeUploadDoc = (file: UploadFile): boolean => {
        if (file.size) {
            const isLt12M = file.size / 1024 / 1024 < 12;
            if (!isLt12M) {
                message.error('File must smaller than 12MB');
            } else {
                // Reset progress when starting new upload
                setIsUploadingImage(true);
                setUploadProgress(0);
            }
            return isLt12M;
        }
        return false;
    };

    const handleImageUploaderChanged = (info: UploadChangeParam) => {
        // Update progress from file.percent whenever onChange fires
        if (info.file && typeof info.file.percent === 'number') {
            // Scale to 0-90% range (upload to GCS)
            setUploadProgress(Math.round(info.file.percent * 0.9));
        }

        if (info.file.status === 'uploading') {
            setIsUploadingImage(true);
        } else if (info.file.status === 'done') {
            // Upload to GCS is done (90%), now send message (90-100%)
            setUploadProgress(90);
            
            const imageUrls = {
                name: info.file.name,
                url: info.file.response[0].path,
            };
            
            // Set state first
            setChatTextAreaValue(' ');
            setChatImageUrls(imageUrls);
            
            // Auto send message after upload - pass imageUrls directly to avoid state timing issues
            // This ensures the imageUrls are available even if state hasn't updated yet
            setTimeout(() => {
                sendChatMessage(imageUrls);
            }, 200);
        } else if (info.file.status === 'error') {
            setIsUploadingImage(false);
            setUploadProgress(0);
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    const getAvatarSrc = (user: any, size: number = 64): string => {
        // If profilePicUrl exists and is not empty, use it
        // Otherwise, use ui-avatars.com
        if (user?.profilePicUrl && user.profilePicUrl.trim() !== '') {
            return user.profilePicUrl;
        }
        return getUserAvatarUrl(user, size);
    };

    const fittedChatRooms = useMemo(() => {
        const memberId = getCurrentMemberId();
        if (!memberId || !chatrooms || chatrooms.length === 0) {
            return [];
        }

        return chatrooms
            .map(chatroom => {
            let notMeUser = chatroom.userA;
            
            if (chatroom.userAId === memberId) {
                notMeUser = chatroom.userB;
            }

            return { ...chatroom, notMeUser };
            })
            .filter(n => {
            if (!filteredNotUserName) return true;
            const name = n.notMeUser?.name || n.notMeUser?.email || '';
            return name.toLowerCase().includes(filteredNotUserName.toLowerCase());
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatrooms, filteredNotUserName]);

    const handleClose = () => {
        closeFloatingChatroom();
    };

    const handleBackToContacts = () => {
        // On mobile, go back to contact list
        if (window.innerWidth <= 768) {
            setShowContactListOnMobile(true);
            setSelectedChatRoom(null);
            selectedChatRoomRef.current = null;
            setChatMessages([]);
            setChatTextAreaValue('');
            setChatImageUrls(null);
            setTextAreaRow(1);
        }
    };

    const countChatroomUserNotification = useCallback(async () => {
        if (chatrooms.length === 0) {
            setUserNotificationMap([]);
            return;
        }

        let queryStr = '';
        for (let index = 0; index < chatrooms.length; index++) {
            const chatroom = chatrooms[index];
            if (index === 0) {
                queryStr = `chatroomIds[]=${chatroom.id}`;
            } else {
                queryStr = `${queryStr}&chatroomIds[]=${chatroom.id}`;
            }
        }

        try {
            const response = await countByChatrooms(queryStr);
            if (response.data) {
                setUserNotificationMap(response.data);
            }
        } catch (error) {
            console.error('FloatingChatroom: Error counting chatroom notifications:', error);
        }
    }, [chatrooms]);

    // Use refs to avoid dependency loop in runMultipleAck
    const userNotificationMapRef = useRef(userNotificationMap);
    useEffect(() => {
        userNotificationMapRef.current = userNotificationMap;
    }, [userNotificationMap]);

    // Use ref to store latest getCurrentMemberId to avoid dependency loop
    const getCurrentMemberIdRef = useRef(getCurrentMemberId);
    useEffect(() => {
        getCurrentMemberIdRef.current = getCurrentMemberId;
    }, [getCurrentMemberId]);

    const runMultipleAck = useCallback(
        async (chatRoomId: number) => {
            const userNotif = userNotificationMapRef.current.find(n => n.chatroomId === chatRoomId);

        if (!userNotif || !userNotif.userNotifIds || userNotif.userNotifIds.length === 0) {
            console.log('FloatingChatroom: No notifications to ack for chatroom:', chatRoomId);
            return false;
        }

        try {
            await multipleRemove(userNotif.userNotifIds);
                console.log(
                    'FloatingChatroom: Removed notifications for chatroom:',
                    chatRoomId,
                    'ids:',
                    userNotif.userNotifIds
                );
            
            // Refresh chatroom list and notification count
                const memberId = getCurrentMemberIdRef.current();
            if (memberId) {
                try {
                    const response = await chatRoomApi.index(
                        { sortBy: 'updatedAt', sortOrientation: 'DESC' },
                        memberId
                    );
                    const { data } = response;
                    if (data && data.data && Array.isArray(data.data)) {
                            // Use functional update to avoid triggering unnecessary re-renders
                            setChatrooms(prevChatrooms => {
                                // Only update if data actually changed
                                const prevIds = prevChatrooms.map(cr => cr.id).sort().join(',');
                                const newIds = data.data.map((cr: ChatRoom) => cr.id).sort().join(',');
                                if (prevIds !== newIds) {
                                    return data.data;
                                }
                                return prevChatrooms;
                            });
                    }
                } catch (error) {
                        console.error(
                            'FloatingChatroom: Error refreshing chatrooms after ack:',
                            error
                        );
                }
            }
            
                // Use ref to call countChatroomUserNotification to avoid dependency loop
                await countChatroomUserNotificationRef.current();
            return true;
        } catch (error) {
            console.error('FloatingChatroom: Error running multiple ack:', error);
            return false;
        }
        },
        // Remove dependencies that cause loops - use refs instead
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleKeyDownEvent = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendChatMessage();
        } else if (e.key === 'Enter' && e.shiftKey) {
            if (textAreaRow <= 3) {
                setTextAreaRow(textAreaRow + 1);
            }
        }
    };

    const sendChatMessage = async (imageUrlsOverride?: any) => {
        const imageUrlsToUse = imageUrlsOverride || chatImageUrls;
        if (!chatTextAreaValue.trim() && !imageUrlsToUse) return;
        if (selectedChatRoom === null) return;
        if (onLoadingSendMessage) return;

        setOnLoadingSendMessage(true);

        // Capture the current state to avoid stale closure issues
        // Declare outside try-catch so it's accessible in catch block
        const wasUploadingImage = isUploadingImage;

        try {
            // Update progress to 95% when starting to send message
            if (wasUploadingImage) {
                setUploadProgress(95);
            }

            const memberId = getCurrentMemberId();
            if (!memberId) {
                console.error('No member ID found');
                setOnLoadingSendMessage(false);
                if (wasUploadingImage) {
                    setIsUploadingImage(false);
                    setUploadProgress(0);
                }
                return;
            }

            let notMeUser = selectedChatRoom.userA;
            if (selectedChatRoom.userAId === memberId) {
                notMeUser = selectedChatRoom.userB;
            }

            const receiverId = notMeUser.id || notMeUser.sn;

            const response = await chatMessageApi.sendMessage({
                message: chatTextAreaValue.trim() || (imageUrlsToUse ? ' ' : ''),
                receiverId: receiverId,
                imageUrls: imageUrlsToUse,
            } as any);

            if (response.data && !response.error) {
                // Message sent successfully - update progress to 100%
                // Use the captured value to ensure we update even if state changed
                if (wasUploadingImage) {
                    setUploadProgress(100);
                }

                // API returns { data: ChatMessageType }, so we need to access response.data.data
                // Type assertion needed because MutateResponseType.data is ChatMessageType | ChatRoomType
                const responseData = response.data as any;
                const newMessage = responseData.data || responseData;
                
                // Only add message if it belongs to the current selected chat room
                if (newMessage && newMessage.chatRoomId === selectedChatRoom.id) {
                    setChatMessages([...chatMessages, newMessage]);
                    setChatTextAreaValue('');
                    // Only reset chatImageUrls if we're not using override
                    if (!imageUrlsOverride) {
                        setChatImageUrls(null);
                    } else {
                        // If using override, reset after sending
                        setChatImageUrls(null);
                    }
                    setTextAreaRow(1);

                    // Scroll to bottom
                    setTimeout(() => {
                        if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop =
                                chatContainerRef.current.scrollHeight;
                        }
                    }, 100);
                } else {
                    // If message belongs to different room, refetch messages
                    fetchChatMessages(selectedChatRoom.id);
                }

                // Reset progress and loading state after a short delay
                setTimeout(() => {
                    if (wasUploadingImage) {
                        setIsUploadingImage(false);
                        setUploadProgress(0);
                    }
                }, 500);
            } else if (response.error) {
                console.error('Error sending message:', response.error);
                if (wasUploadingImage) {
                    setIsUploadingImage(false);
                    setUploadProgress(0);
                }
            } else {
                // If response doesn't have data or error, still mark as complete if we were uploading
                if (wasUploadingImage) {
                    setUploadProgress(100);
                    setTimeout(() => {
                        setIsUploadingImage(false);
                        setUploadProgress(0);
                    }, 500);
                }
            }
        } catch (error) {
            console.error('Error sending chat message:', error);
            if (wasUploadingImage) {
                setIsUploadingImage(false);
                setUploadProgress(0);
            }
        } finally {
            setOnLoadingSendMessage(false);
        }
    };

    const fetchChatMessages = useCallback(
        async (chatRoomId: number) => {
        console.log('FloatingChatroom: fetchChatMessages called for chatRoomId:', chatRoomId);
        setOnLoadingChatMessage(true);
        const memberId = getCurrentMemberId();
        
        if (!memberId) {
            console.error('FloatingChatroom: No memberId found, cannot fetch chat messages');
            setOnLoadingChatMessage(false);
            return;
        }

        if (!chatRoomId) {
                console.error(
                    'FloatingChatroom: No chatRoomId provided, cannot fetch chat messages'
                );
            setOnLoadingChatMessage(false);
            return;
        }

        console.log('FloatingChatroom: Fetching messages with query:', {
                'findBy-chatRoomId': `eq,chatRoomId,${chatRoomId},number`,
        });

        try {
            // Match public frontend format:
            // chatMessageApi.index({ 'findBy-chatRoomId': `eq,chatRoomId,${this.selectedChatRoom.id},number` }, this.memberId)
            const query = {
                    'findBy-chatRoomId': `eq,chatRoomId,${chatRoomId},number`,
            };
            
            const response = await chatMessageApi.index(query);
            
            if (response.data && !response.error) {
                // Response structure: { data: { data: [...] } }
                // Public frontend: this.chatMessages = data.data;
                // Type assertion needed because MutateResponseType.data is ChatMessageType | ChatRoomType
                const responseData = response.data as any;
                const messages = responseData.data || [];

                console.log('FloatingChatroom: Received messages count:', messages.length);

                // Verify all messages belong to this chat room
                const filteredMessages = messages.filter((msg: ChatMessage) => {
                    return msg.chatRoomId === chatRoomId;
                });
                
                    console.log(
                        'FloatingChatroom: Filtered messages count:',
                        filteredMessages.length
                    );
                console.log('FloatingChatroom: Setting chatMessages:', filteredMessages);
                setChatMessages(filteredMessages);
                
                // Scroll to bottom after messages are loaded
                // Use requestAnimationFrame + setTimeout for better timing
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        if (chatContainerRef.current) {
                            const container = chatContainerRef.current;
                            container.scrollTop = container.scrollHeight;
                                console.log(
                                    'FloatingChatroom: Scrolled to bottom after fetch, scrollTop:',
                                    container.scrollTop,
                                    'scrollHeight:',
                                    container.scrollHeight
                                );
                        }
                    }, 100);
                });
            } else if (response.error) {
                console.error('Error fetching chat messages:', response.error);
                setChatMessages([]);
            } else {
                console.warn('No data in response:', response);
                setChatMessages([]);
            }
        } catch (error) {
            console.error('Error fetching chat messages:', error);
            setChatMessages([]);
        } finally {
            setOnLoadingChatMessage(false);
        }
        },
        [getCurrentMemberId]
    );

    const handleSelectChatRoom = useCallback(
        async (chatroom: ChatRoom) => {
            console.log('FloatingChatroom: handleSelectChatRoom called for chatroom:', chatroom.id);
            console.log('FloatingChatroom: Current selectedChatRoom:', selectedChatRoom?.id);
            console.log('FloatingChatroom: chat.isOpen:', chat.isOpen);

            // On mobile, hide contact list when selecting a chat room
            if (window.innerWidth <= 768) {
                setShowContactListOnMobile(false);
            }

            // Always reset chat messages first to avoid showing wrong messages
            // This ensures fresh data is loaded even if selecting the same chat room
            setChatMessages([]);
            setSelectedChatRoom(chatroom);
            selectedChatRoomRef.current = chatroom; // Update ref
            setChatTextAreaValue('');
            setChatImageUrls(null);
            setTextAreaRow(1);

            // Ack all notifications for this chatroom when selected (like public-frontend)
            if (chat.isOpen) {
                console.log('FloatingChatroom: Running multiple ack for chatroom:', chatroom.id);
                await runMultipleAck(chatroom.id);
            }

            // Always fetch messages for the selected room, even if it's the same one
            // This ensures messages are always loaded when chat room is opened
            console.log('FloatingChatroom: About to fetch messages for chatroom:', chatroom.id);
            try {
                await fetchChatMessages(chatroom.id);
                console.log(
                    'FloatingChatroom: Successfully fetched messages for chatroom:',
                    chatroom.id
                );
            } catch (error) {
                console.error(
                    'FloatingChatroom: Error fetching messages for chatroom:',
                    chatroom.id,
                    error
                );
            }
        },
        [selectedChatRoom, chat.isOpen, runMultipleAck, fetchChatMessages]
    );

    const handlingNewChatMessageEvent = useCallback(
        async (event: Event) => {
        const messageEvent = event as MessageEvent;
        console.log('FloatingChatroom: handlingNewChatMessageEvent called', messageEvent);
        console.log('FloatingChatroom: event.data:', messageEvent.data);

        try {
            const parsedData = JSON.parse(messageEvent.data);
            console.log('FloatingChatroom: parsedData:', parsedData);
            
            const content = JSON.parse(parsedData.notif.message);
            console.log('FloatingChatroom: content:', content);
            
            const notificationData = JSON.parse(content.content);
            console.log('FloatingChatroom: notificationData:', notificationData);

            // Always refresh chat room list to show new messages
            const memberId = getCurrentMemberId();
            
            if (memberId) {
                try {
                    const response = await chatRoomApi.index(
                        { sortBy: 'updatedAt', sortOrientation: 'DESC' },
                        memberId
                    );

                    const { data } = response;
                    if (data && data.data && Array.isArray(data.data)) {
                        const updatedChatrooms = data.data;
                        setChatrooms(updatedChatrooms);

                        if (selectedChatRoomRef.current) {
                                const updatedChatRoom = updatedChatrooms.find(
                                    (cr: ChatRoom) => cr.id === selectedChatRoomRef.current!.id
                                );
                            if (updatedChatRoom) {
                                let notMeUser = updatedChatRoom.userA;
                                if (updatedChatRoom.userAId === memberId) {
                                    notMeUser = updatedChatRoom.userB;
                                }
                                const updatedSelected = { ...updatedChatRoom, notMeUser };
                                setSelectedChatRoom(updatedSelected);
                                selectedChatRoomRef.current = updatedSelected;
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error refreshing chat rooms:', error);
                }
            }

            // Check if message belongs to selected chat room using ref
            const currentSelected = selectedChatRoomRef.current;
            if (currentSelected !== null) {
                    const chatRoomId =
                        notificationData.carry &&
                        notificationData.carry.ChatMessage &&
                        notificationData.carry.ChatMessage.chatRoomId;
                console.log('FloatingChatroom: chatRoomId from notification:', chatRoomId);
                console.log('FloatingChatroom: selectedChatRoom.id:', currentSelected.id);

                if (chatRoomId && currentSelected.id === chatRoomId) {
                    console.log('FloatingChatroom: Refreshing chat messages for selected room');
                    if (fetchChatMessagesRef.current) {
                        await fetchChatMessagesRef.current(currentSelected.id);
                        
                        // Play notification sound
                        playChatNotificationSound();

                        // Auto ack on selected chatroom (like public-frontend)
                        if (chat.isOpen) {
                            await runMultipleAck(currentSelected.id);
                        }

                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                if (chatContainerRef.current) {
                                    const container = chatContainerRef.current;
                                    container.scrollTop = container.scrollHeight;
                                        console.log(
                                            'FloatingChatroom: Scrolled to bottom after realtime update, scrollTop:',
                                            container.scrollTop,
                                            'scrollHeight:',
                                            container.scrollHeight
                                        );
                                }
                            }, 200);
                        });
                    }
                } else {
                        console.log(
                            'FloatingChatroom: Message is for different chat room:',
                            chatRoomId,
                            'selected:',
                            currentSelected.id
                        );
                }
            } else {
                console.log('FloatingChatroom: No chat room selected, only updating list');
            }
        } catch (error) {
            console.error('FloatingChatroom: Error handling new chat message event:', error);
        }
        },
        [chat.isOpen, getCurrentMemberId, runMultipleAck]
    );

    const listenOnNewChatMessage = useCallback(() => {
        const memberId = getCurrentMemberId();
        console.log('FloatingChatroom: listenOnNewChatMessage called, memberId:', memberId);

        if (!memberId) {
            console.log('FloatingChatroom: No memberId, cannot setup EventSource');
            return;
        }

        const userNotificationSubs = [`from_user.chat_message.received_by_${memberId}.created`];

        const topicsStr = userNotificationSubs.map(sub => `topics[]=${sub}`).join('&');

        if (!newChatMessageEventRef.current) {
            const token = localStorage.getItem('accessToken');
            if (!token) {
                console.log('FloatingChatroom: No token, cannot setup EventSource');
                return;
            }

            try {
                const url = `${process.env.REACT_APP_API_URL}/api/v2/notifications/listen?${topicsStr}`;
                console.log('FloatingChatroom: Creating EventSource:', url);

                const xhrHeaders = {
                    Authorization: `Bearer ${token}`,
                    Accept: 'text/event-stream',
                    'Cache-Control': 'no-cache',
                    'X-Requested-With': 'XMLHttpRequest',
                };

                const eventSource = new EventSourcePolyfill(url, {
                    xhrHeaders: xhrHeaders,
                    withCredentials: false,
                });

                eventSource.addEventListener('ping', () => {
                    console.log('FloatingChatroom: EventSource ping received');
                });
                eventSource.addEventListener('signal', handlingNewChatMessageEvent);
                eventSource.addEventListener('open', () => {
                    console.log('FloatingChatroom: EventSource opened successfully');
                });
                eventSource.addEventListener('error', (error: any) => {
                    const errorMessage = error?.message || error?.toString() || '';
                    if (
                        errorMessage.includes('ECONNRESET') ||
                        errorMessage.includes('socket hang up') ||
                        errorMessage.includes('Connection closed') ||
                        errorMessage.includes('read ECONNRESET')
                    ) {
                        return;
                    }
                });

                newChatMessageEventRef.current = eventSource;
                console.log('FloatingChatroom: EventSource created and stored in ref');
            } catch (error) {
                console.error('FloatingChatroom: Error setting up EventSource:', error);
            }
        } else {
            console.log('FloatingChatroom: EventSource already exists, skipping creation');
        }
    }, [handlingNewChatMessageEvent, getCurrentMemberId]);

    // Reset state when chat room closes / opens
    useEffect(() => {
        if (!chat.isOpen) {
            console.log('FloatingChatroom: Chat room closed, resetting state');
            setSelectedChatRoom(null);
            selectedChatRoomRef.current = null;
            setChatMessages([]);
            setChatTextAreaValue('');
            setChatImageUrls(null);
            setTextAreaRow(1);
            setFilteredNotUserName('');
            setShowContactListOnMobile(true); // ensure contact list is shown next time on mobile
            
            // Re-enable body scroll on mobile
            if (window.innerWidth <= 768) {
                document.body.classList.remove('chatroom-open');
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
            }
        } else {
            // When chat just opened and no active chat, show contact list on mobile
            if (window.innerWidth <= 768 && !selectedChatRoom) {
                setShowContactListOnMobile(true);
                // Disable body scroll on mobile to prevent background scrolling
                document.body.classList.add('chatroom-open');
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
            }
        }
    }, [chat.isOpen, selectedChatRoom]);

    // Count notifications when chatrooms change (like public-frontend)
    // Use ref to store latest countChatroomUserNotification to avoid dependency loop
    const countChatroomUserNotificationRef = useRef(countChatroomUserNotification);
    useEffect(() => {
        countChatroomUserNotificationRef.current = countChatroomUserNotification;
    }, [countChatroomUserNotification]);

    useEffect(() => {
        if (chat.isOpen && chatrooms.length > 0) {
            countChatroomUserNotificationRef.current();
        }
        // Only depend on chat.isOpen and chatrooms.length, not the callback itself
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.isOpen, chatrooms.length]);

    // Use ref to store latest handleSelectChatRoom to avoid dependency loop
    const handleSelectChatRoomRef = useRef(handleSelectChatRoom);
    useEffect(() => {
        handleSelectChatRoomRef.current = handleSelectChatRoom;
    }, [handleSelectChatRoom]);

    useEffect(() => {
        if (!chat.isOpen) return;
        
        const indexListItem = async () => {
            setOnLoadingChatroom(true);
            const memberId = getCurrentMemberIdRef.current();
            
            if (!memberId) {
                setOnLoadingChatroom(false);
                return;
            }

            try {
                const response = await chatRoomApi.index(
                    { sortBy: 'updatedAt', sortOrientation: 'DESC' },
                    memberId
                );

                const { data } = response;

                if (data && data.data) {
                    const chatroomsData = data.data;

                    if (Array.isArray(chatroomsData)) {
                        setChatrooms(chatroomsData);

                        if (chat.proxyChatRoomId !== -1) {
                            const chatroom = chatroomsData.find(
                                (n: ChatRoom) => n.id === chat.proxyChatRoomId
                            );
                            if (chatroom) {
                                resetProxyChatRoomId();
                                let notMeUser = chatroom.userA;
                                if (chatroom.userAId === memberId) {
                                    notMeUser = chatroom.userB;
                                }
                                const populatedChatRoom = { ...chatroom, notMeUser };
                                selectedChatRoomRef.current = populatedChatRoom;
                                handleSelectChatRoomRef.current(populatedChatRoom);
                            }
                        }
                    } else {
                        setChatrooms([]);
                    }
                } else {
                    setChatrooms([]);
                }
            } catch (error) {
                console.error('Error fetching chat rooms:', error);
                setChatrooms([]);
            } finally {
                setOnLoadingChatroom(false);
            }
        };
        
        indexListItem();
        // Only depend on chat.isOpen and chat.proxyChatRoomId, not the callbacks
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.isOpen, chat.proxyChatRoomId]);

    useEffect(() => {
        if (chatContainerRef.current && selectedChatRoom && chatMessages.length > 0) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (chatContainerRef.current) {
                        const container = chatContainerRef.current;
                        container.scrollTop = container.scrollHeight;
                        console.log(
                            'FloatingChatroom: Scrolled to bottom, scrollTop:',
                            container.scrollTop,
                            'scrollHeight:',
                            container.scrollHeight
                        );
                    }
                }, 100);
            });
        }
    }, [chatMessages, selectedChatRoom]);

    useEffect(() => {
        selectedChatRoomRef.current = selectedChatRoom;
        fetchChatMessagesRef.current = fetchChatMessages;
    }, [selectedChatRoom, fetchChatMessages]);

    // Use ref to store latest listenOnNewChatMessage to avoid dependency loop
    const listenOnNewChatMessageRef = useRef(listenOnNewChatMessage);
    useEffect(() => {
        listenOnNewChatMessageRef.current = listenOnNewChatMessage;
    }, [listenOnNewChatMessage]);

    useEffect(() => {
        // Setup EventSource when chat is open (similar to public-frontend mounted hook)
        if (chat.isOpen) {
            const timer = setTimeout(() => {
                console.log('FloatingChatroom: Calling listenOnNewChatMessage after delay');
                listenOnNewChatMessageRef.current();
            }, 500);

            return () => {
                clearTimeout(timer);
                // Only cleanup EventSource when component unmounts, not when chat closes
                // This ensures EventSource stays active as long as component is mounted
            };
        }
        // Only depend on chat.isOpen, not the callback itself
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.isOpen]);

    useEffect(() => {
        // Cleanup EventSource only on component unmount
        return () => {
            console.log('FloatingChatroom: Component unmounting, cleaning up EventSource');
            if (newChatMessageEventRef.current) {
                newChatMessageEventRef.current.removeEventListener('ping', () => {});
                newChatMessageEventRef.current.removeEventListener(
                    'signal',
                    handlingNewChatMessageEvent
                );
                newChatMessageEventRef.current.close();
                newChatMessageEventRef.current = null;
            }
            
            // Re-enable body scroll on unmount
            document.body.classList.remove('chatroom-open');
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!chat.isOpen) {
        return null;
    }

    const isNoActiveChat = selectedChatRoom === null;
    const checkIsSelectedChatroom = (chatroom: ChatRoom) => {
        return selectedChatRoom?.id === chatroom.id;
    };

    const isHaveNewUserMessage = (chatRoomId: number): boolean => {
        const userNotif = userNotificationMap.find(n => n.chatroomId === chatRoomId);
        if (!userNotif) return false;
        return userNotif.notifCount > 0;
    };

    const getNotificationCount = (chatRoomId: number): number => {
        const userNotif = userNotificationMap.find(n => n.chatroomId === chatRoomId);
        if (!userNotif) return 0;
        return userNotif.notifCount;
    };

    // Log current chat open state for debugging (throttled to avoid spam)
    renderCountRef.current += 1;
    if (renderCountRef.current % 10 === 0) {
        console.log('FloatingChatroom: render, chat.isOpen =', chat.isOpen, 'render count:', renderCountRef.current);
    }

    // Force full screen on mobile with aggressive inline styles
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    
    return (
        <div 
            css={isMobile ? undefined : [styles.floatingChatroom, styles.mobileOverrides]}
            style={
                isMobile
                    ? {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                maxHeight: '100vh',
                margin: 0,
                padding: 0,
                zIndex: 99999,
                backgroundColor: '#ffffff',
                borderRadius: 0,
                transform: 'none',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                      }
                    : undefined
            }
        >
            <div 
                css={isMobile ? undefined : styles.floatingWrapper}
                style={
                    isMobile
                        ? {
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    flex: 1,
                          }
                        : undefined
                }
            >
                <div 
                    css={isMobile ? undefined : styles.contactContainer} 
                    className={showContactListOnMobile ? 'show-on-mobile' : ''}
                    style={
                        isMobile
                            ? {
                        width: '100%',
                        height: '100%',
                        display: showContactListOnMobile ? 'flex' : 'none',
                        flexDirection: 'column',
                        position: 'relative',
                        flex: 1,
                        overflow: 'hidden',
                              }
                            : undefined
                    }
                >
                    <div css={styles.filterist}>
                        <div css={styles.lftTopContent}>
                            <h1>Chat</h1>
                            <CloseOutlined css={styles.mobileCloseIcon} onClick={handleClose} />
                        </div>
                        <div css={styles.lftBottomContent}>
                            <Input
                                placeholder="Find user"
                                suffix={<SearchOutlined />}
                                style={isMobile ? { width: '100%' } : { width: 150 }}
                                value={filteredNotUserName}
                                onChange={e => setFilteredNotUserName(e.target.value)}
                            />
                        </div>
                    </div>
                    {onLoadingChatroom ? (
                        <div css={styles.contactList}>
                            {[1, 2, 3, 4, 5, 6].map(item => (
                                <div key={`chatroom-skel-${item}`} css={styles.contactItem}>
                                    <div css={styles.chatContent}>
                                        <div css={styles.chatName}>
                                            <Spin size="small" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div css={styles.contactList}>
                            {fittedChatRooms.length === 0 && (
                                <div
                                    style={{
                                        padding: '16px',
                                        textAlign: 'center',
                                        color: '#8d96aa',
                                    }}
                                >
                                    No chat rooms found
                                </div>
                            )}
                            {fittedChatRooms.map(chatroom => (
                                <div
                                    key={`chatroom-${chatroom.id}`}
                                    css={[
                                        styles.contactItem,
                                        checkIsSelectedChatroom(chatroom) &&
                                            styles.selectedChatroom,
                                    ]}
                                    onClick={() => {
                                        console.log(
                                            'FloatingChatroom: onClick triggered for chatroom:',
                                            chatroom.id
                                        );
                                        console.log(
                                            'FloatingChatroom: Current selectedChatRoom before click:',
                                            selectedChatRoom?.id
                                        );
                                        handleSelectChatRoom(chatroom);
                                    }}
                                >
                                    <div css={styles.chatImgWrapper}>
                                        <img
                                            css={styles.chatImg}
                                            src={getAvatarSrc(chatroom.notMeUser, 26)}
                                            alt={
                                                chatroom.notMeUser?.name ||
                                                chatroom.notMeUser?.email
                                            }
                                            onError={e => {
                                                const target = e.target as HTMLImageElement;
                                                // If image fails to load, use ui-avatars.com as fallback
                                                target.src = getUserAvatarUrl(
                                                    chatroom.notMeUser,
                                                    26
                                                );
                                            }}
                                        />
                                    </div>
                                    <div css={styles.chatContent}>
                                        <p
                                            css={[
                                                styles.chatName,
                                                isHaveNewUserMessage(chatroom.id) &&
                                                    styles.chatNameBold,
                                            ]}
                                        >
                                            {chatroom.notMeUser?.name || chatroom.notMeUser?.email}
                                        </p>
                                    </div>
                                    {isHaveNewUserMessage(chatroom.id) && (
                                        <Badge count={getNotificationCount(chatroom.id)} />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div 
                    css={isMobile ? undefined : styles.chatroomContainer} 
                    className={!isNoActiveChat ? 'show-chatroom' : ''}
                    style={
                        isMobile
                            ? {
                        width: '100%',
                        height: '100%',
                        display: !isNoActiveChat ? 'flex' : 'none',
                        flexDirection: 'column',
                        position: 'relative',
                        flex: 1,
                        overflow: 'hidden',
                        borderLeft: 'none',
                              }
                            : undefined
                    }
                >
                    <div css={styles.leftWrapper}>
                        <div css={[styles.topist, isNoActiveChat && styles.noActiveChatTopist]}>
                            {!isNoActiveChat && selectedChatRoom && (
                                <div css={styles.chatUserWrapper}>
                                    <LeftOutlined
                                        css={styles.backToContacts}
                                        onClick={handleBackToContacts}
                                    />
                                    <div css={styles.userAvatar}>
                                        <img
                                            css={styles.headerImg}
                                            src={getAvatarSrc(selectedChatRoom.notMeUser, 36)}
                                            alt={
                                                selectedChatRoom.notMeUser?.name ||
                                                selectedChatRoom.notMeUser?.email
                                            }
                                            onError={e => {
                                                const target = e.target as HTMLImageElement;
                                                // If image fails to load, use ui-avatars.com as fallback
                                                target.src = getUserAvatarUrl(
                                                    selectedChatRoom.notMeUser,
                                                    36
                                                );
                                            }}
                                        />
                                    </div>
                                    <div css={styles.userInfo}>
                                        <div css={styles.userName}>
                                            <p>
                                                {selectedChatRoom.notMeUser?.name ||
                                                    selectedChatRoom.notMeUser?.email}
                                            </p>
                                        </div>
                                        <p>
                                            {selectedChatRoom.notMeUser?.ProfessionDetailId &&
                                            selectedChatRoom.notMeUser?.ProfessionDetail
                                                ?.businessName
                                                ? `Business Owner: ${selectedChatRoom.notMeUser.ProfessionDetail.businessName}`
                                                : 'Propertigrab Member'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div css={styles.closeWrapper}>
                                <DownOutlined onClick={handleClose} style={{ cursor: 'pointer' }} />
                            </div>
                        </div>
                        {isNoActiveChat ? (
                            <div css={styles.emptyChatroomWrapper}>
                                <Empty description="Select the chatroom on the left to start chatting with the other member." />
                            </div>
                        ) : (
                            <div css={styles.chatroomWrapper}>
                                {onLoadingChatMessage ? (
                                    <div css={styles.chatroom}>
                                        <Spin />
                                    </div>
                                ) : (
                                    <div css={styles.chatroom} ref={chatContainerRef}>
                                        <div css={styles.chatbox}>
                                            {chatMessages.map(message => (
                                                    <div
                                                    key={`chat-message-${message.id}`}
                                                    css={[
                                                        styles.chatItem,
                                                        message.senderId === getCurrentMemberId()
                                                            ? styles.chatItemRight
                                                            : styles.chatItemLeft,
                                                    ]}
                                                >
                                                    <div
                                                        css={[
                                                            styles.chatBubble,
                                                            message.senderId ===
                                                            getCurrentMemberId()
                                                                ? styles.chatBubbleRight
                                                                : styles.chatBubbleLeft,
                                                        ]}
                                                    >
                                                        {message.imageUrls ? (
                                                            <div css={styles.chatImage}>
                                                                <img 
                                                                    src={
                                                                        getImageUrl(
                                                                            message.imageUrls
                                                                        ) || ''
                                                                    }
                                                                    alt="Chat message attachment"
                                                                    onClick={() => {
                                                                        const url = getImageUrl(
                                                                            message.imageUrls
                                                                        );
                                                                        if (url)
                                                                            window.open(
                                                                                url,
                                                                                '_blank'
                                                                            );
                                                                    }}
                                                                    css={styles.chatImageImg}
                                                                />
                                                                <div css={styles.chatTimeImage}>
                                                                    <div
                                                                        css={
                                                                            styles.chatImageContainer
                                                                        }
                                                                    >
                                                                        <span
                                                                            css={
                                                                                styles.chatImageSpan
                                                                            }
                                                                        >
                                                                            {new Date(
                                                                                message.createdAt
                                                                            ).toLocaleTimeString(
                                                                                'en-US',
                                                                                {
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                }
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                css={
                                                                    message.senderId ===
                                                                    getCurrentMemberId()
                                                                        ? styles.chatBubbleInnerRight
                                                                        : styles.chatBubbleInnerLeft
                                                                }
                                                            >
                                                                <div
                                                                    css={
                                                                        message.senderId ===
                                                                        getCurrentMemberId()
                                                                            ? styles.chatTextRight
                                                                            : styles.chatText
                                                                    }
                                                                >
                                                                    <span>{message.message}</span>
                                                                </div>
                                                                <div
                                                                    css={
                                                                        message.senderId ===
                                                                        getCurrentMemberId()
                                                                            ? styles.chatTimeStatusRight
                                                                            : styles.chatTimeStatus
                                                                    }
                                                                >
                                                                    <div
                                                                        css={
                                                                            styles.chatTimeStatusInner
                                                                        }
                                                                    >
                                                                        <span css={styles.timeText}>
                                                                            {new Date(
                                                                                message.createdAt
                                                                            ).toLocaleTimeString(
                                                                                'en-US',
                                                                                {
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                }
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                    </div>
                                ))}
                                            {/* Upload Progress Chat Bubble */}
                                            {isUploadingImage && (
                                                <div css={[styles.chatItem, styles.chatItemRight]}>
                                                    <div
                                                        css={[
                                                            styles.chatBubble,
                                                            styles.chatBubbleRight,
                                                        ]}
                                                    >
                                                        <div
                                                            css={[
                                                                styles.chatBubbleInnerRight,
                                                                styles.chatUploadProgressBubble,
                                                            ]}
                                                        >
                                                            <div css={styles.uploadProgressCircle}>
                                                                <Progress 
                                                                    type="circle" 
                                                                    percent={uploadProgress} 
                                                                    width={60}
                                                                    strokeWidth={6}
                                                                    format={percent => (
                                                                        <span
                                                                            css={
                                                                                styles.progressPercent
                                                                            }
                                                                        >
                                                                            {percent}%
                                                                        </span>
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {!isNoActiveChat && (
                                    <div css={styles.actionContainer}>
                                        <div css={styles.actionWrapper}>
                                            <div css={styles.inputContainer}>
                                                <div css={styles.textareaContainer}>
                                                    <div css={styles.chatboxInput}>
                                                        <Upload
                                                            css={styles.imageUploader}
                                                            action={uploaderInfo.url}
                                                            headers={uploaderInfo.headers}
                                                            multiple={false}
                                                            showUploadList={false}
                                                            beforeUpload={beforeUploadDoc}
                                                            onChange={handleImageUploaderChanged}
                                                        >
                                                            <Button
                                                                shape="circle"
                                                                size="small"
                                                                icon={
                                                                    !isUploadingImage ? (
                                                                        <PictureOutlined />
                                                                    ) : undefined
                                                                }
                                                                loading={isUploadingImage}
                                                            />
                                                        </Upload>
                                                        <textarea
                                                            css={styles.chatTextbox}
                                                            rows={textAreaRow}
                                                            placeholder="Tulis pesan..."
                                                            value={chatTextAreaValue}
                                                            onChange={e =>
                                                                setChatTextAreaValue(e.target.value)
                                                            }
                                                            onKeyDown={handleKeyDownEvent}
                                                            disabled={onLoadingSendMessage}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                css={styles.btnSendMessage}
                                                type="button"
                                                onClick={() => sendChatMessage()}
                                                disabled={
                                                    onLoadingSendMessage ||
                                                    (!chatTextAreaValue.trim() && !chatImageUrls)
                                                }
                                            >
                                                <svg
                                                    css={styles.sendIcon}
                                                    viewBox="0 0 24 24"
                                                    width="22"
                                                    height="22"
                                                    fill="#FFFFFF"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M21.35 11.32 5.66 3.19a1.81 1.81 0 0 0-1.81.28 1.8 1.8 0 0 0-.55 1.77L4.72 12 3.3 18.71a1.75 1.75 0 0 0 .7 1.81 1.68 1.68 0 0 0 1 .34c.266 0 .528-.069.76-.2l15.61-8a.759.759 0 0 0 .41-.67.77.77 0 0 0-.43-.67ZM5 19.34a.14.14 0 0 1-.15 0 .27.27 0 0 1-.1-.27L6 13.36l6.8-.87a.5.5 0 0 0 0-1L6 10.61 4.76 4.93a.33.33 0 0 1 .07-.32.32.32 0 0 1 .17-.06L19.36 12 5 19.34Z"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FloatingChatroom;
